import type {Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import {useCallback} from 'react'
import {useQueryClient} from '@github-ui/react-query'

import {fetchRepos} from './fetch-repos'

/**
 * Returns an imperative repository loader backed by TanStack Query's cache.
 *
 * `@github-ui/repos-selector` drives loading itself via an imperative
 * `repositoryLoader(query)` callback, so rather than a declarative `useQuery`
 * we route through `queryClient.fetchQuery` — this still dedupes and caches
 * results per query while handing the selector the promise it expects. Each
 * distinct query is cached independently under the
 * `['copilot-repos', owner, query]` key.
 *
 * `owner` scopes the blank-query default list to a single account and is part
 * of the cache key: a blank query normalizes to the same string under every
 * scope, so omitting the owner would serve one scope's repositories to another
 * until the entry goes stale.
 */
export function useRepoLoader(owner?: string | null): (query: string) => Promise<Repository[]> {
  const queryClient = useQueryClient()
  return useCallback(
    (query: string) => {
      const normalizedQuery = query.trim()
      return queryClient.fetchQuery({
        queryKey: ['copilot-repos', owner ?? '', normalizedQuery],
        queryFn: ({signal}) => fetchRepos(normalizedQuery, signal, owner),
        staleTime: 60 * 1000,
      })
    },
    [queryClient, owner],
  )
}
